import {
  Box,
  Button,
  COUNTRY_RECORDS,
  Flex,
  FormGroup,
  FormLabel,
  Paragraph,
  PencilIcon,
  PhoneInput,
} from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";

import { LazyUserAvatar } from "@/components/avatars";
import { VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { PHONE_NUMBER_MAX_LENGTH } from "@/data/job-applications";
import { formatFullName } from "@/data/persons";
import { formatPhone } from "@/data/phones";
import { isErrorVisible } from "@/formikComponents";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import { ApplyContactPersonModal } from "@/views/job-applications/apply/_details/ApplyContactPersonModal";
import { DetailsFormValues } from "@/views/job-applications/apply/_details/ApplyDetailsForm";

interface ApplyApplicationContactProps {
  formik: FormikProps<DetailsFormValues>;
}

export const ApplyApplicationContact: FC<ApplyApplicationContactProps> = ({ formik }) => {
  const { t } = useTranslation(["session", "common"]);

  const { profile } = useApplyWizard();

  const contactPersonModalState = useDialogState({ animated: true });

  const contactPerson = formik.values.contactPerson;

  const [contactPersonPhoneNumber, setContactPersonPhoneNumber] = useState(contactPerson.phone?.phoneNumber);
  const [contactPersonPhonePrefix, setContactPersonPhonePrefix] = useState(contactPerson.phone?.phonePrefix);
  const [isPhoneEditOn, setIsPhoneEditOn] = useState(false);

  useEffect(() => {
    if (isErrorVisible(formik, "contactPerson.phone")) {
      setIsPhoneEditOn(true);
    }
  }, [formik]);

  return (
    <Box border="primary" borderRadius="lg" p="md">
      <Flex gap="xs" justifyContent="space-between" flexDirection={["column", null, "row"]}>
        <Flex gap="xs" flexDirection={["column", null, "row"]}>
          <LazyUserAvatar user={contactPerson} size="md" />
          <Flex flexDirection="column">
            <Paragraph bold ellipsis data-test="contact-fullName">
              {formatFullName(contactPerson)}
            </Paragraph>

            {contactPerson.position?.name && (
              <Paragraph color="n700" data-test="contact-positionName">
                {contactPerson.position.name}
              </Paragraph>
            )}

            <Paragraph color="n700" data-test="contact-email">
              {contactPerson.email}
            </Paragraph>

            <Box pt="sm">
              {!contactPersonPhonePrefix || !contactPersonPhoneNumber || isPhoneEditOn ? (
                <FormGroup
                  sx={{
                    input: { width: "100%" },
                  }}
                  label={
                    <FormLabel
                      label={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER")}
                      required
                    />
                  }
                  error={
                    <React.Fragment>
                      <FormikError
                        name="contactPerson.phone"
                        overrideMessages={{
                          [VALIDATION_TYPES.REQUIRED]: t(
                            "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER_REQUIRED",
                          ),
                        }}
                      />
                      <FormikError
                        name="contactPerson.phone.phoneCode"
                        overrideMessages={{
                          [VALIDATION_TYPES.REQUIRED]: t(
                            "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_PREFIX_REQUIRED",
                          ),
                        }}
                      />
                      <FormikError
                        name="contactPerson.phone.phoneNumber"
                        overrideMessages={{
                          [VALIDATION_TYPES.REQUIRED]: t(
                            "JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER_REQUIRED",
                          ),
                          [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                            value: PHONE_NUMBER_MAX_LENGTH,
                          }),
                        }}
                      />
                    </React.Fragment>
                  }>
                  <PhoneInput
                    invalid={!!isErrorVisible(formik, "contactPerson.phone")}
                    name="contactPerson.phone"
                    defaultCountryCode="se"
                    onChange={value => {
                      formik.setFieldValue(
                        "contactPerson.phone",
                        {
                          phoneCode: value.country?.toUpperCase(),
                          phoneNumber: value.number,
                          phonePrefix: value.code,
                        },
                        true,
                      );
                    }}
                    placeholder={t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.PHONE_NUMBER")}
                    size="xl"
                    value={{
                      number: contactPerson.phone?.phoneNumber ?? undefined,
                      country:
                        contactPerson.phone?.phoneCode ?? contactPerson.location?.suggestedPhoneCode ?? undefined,
                      code:
                        contactPerson.phone?.phonePrefix ??
                        COUNTRY_RECORDS.find(
                          cr => cr.countryCode === contactPerson.location?.suggestedPhoneCode?.toLowerCase(),
                        )?.callingCode ??
                        undefined,
                    }}
                    variant="primary"
                  />
                </FormGroup>
              ) : (
                <Flex flexDirection="row" alignItems="center" gap="xs">
                  <Paragraph color="n700" data-test="contact-phone">
                    {formatPhone(contactPerson.phone)}
                  </Paragraph>
                  <Button size="sm" variant="ghost" iconLeft={<PencilIcon />} onClick={() => setIsPhoneEditOn(true)} />
                </Flex>
              )}
            </Box>
          </Flex>
        </Flex>
        <Box>
          {profile!.companyRole && (
            <Button
              data-test="contact-change"
              size="sm"
              variant="ghost"
              as={DialogDisclosure}
              {...contactPersonModalState}>
              {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.APPLICATION_DETAILS.CHANGE_CONTACT_BUTTON")}
            </Button>
          )}

          <ApplyContactPersonModal
            dialogState={contactPersonModalState}
            setContactPerson={contactPersonProfile => {
              formik.setFieldValue("contactPerson", contactPersonProfile, true);
              setContactPersonPhoneNumber(contactPersonProfile.phone?.phoneNumber);
              setContactPersonPhonePrefix(contactPersonProfile.phone?.phonePrefix);
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
};
